/**
 * Step 3 do wizard ZTNA — Routing & Identity.
 *
 * 3 radio-cards encadeados + upstream URL + permissoes.
 * Conditional: se identity_mode === 'oidc', exibe campo de Redirect URIs
 * (TagsInput multi-string).
 */
import { useEffect } from 'react'
import {
  Alert,
  Anchor,
  Card,
  Collapse,
  Group,
  MultiSelect,
  Radio,
  SegmentedControl,
  Stack,
  Switch,
  TagsInput,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { IconInfoCircle, IconAlertTriangle, IconClockHour4 } from '@tabler/icons-react'
import { TimezoneSelect } from '@/components/ui/TimezoneSelect'
import type { DnsLookupResponse, IdentityMode, Protocol, RoutingMode, TimeWindow } from '../types'
import type { WizardStep3Data } from './wizardState'

interface Props {
  data: WizardStep3Data
  publicHostname?: string
  /** Resultado do dns-lookup do Step 1 — usado pra auto-fill upstream_url. */
  dnsLookup?: DnsLookupResponse
  onChange: (next: WizardStep3Data) => void
}

const PROTOCOL_OPTIONS: { value: Protocol; label: string; desc: string }[] = [
  { value: 'http', label: 'HTTP/HTTPS', desc: 'Web app comum. Reverse proxy.' },
  { value: 'ssh', label: 'SSH', desc: 'Acesso shell via wss tunnel.' },
  { value: 'tcp', label: 'TCP', desc: 'Banco, mensageria, raw socket.' },
]

const ROUTING_OPTIONS: { value: RoutingMode; label: string; desc: string }[] = [
  { value: 'direct', label: 'Direct', desc: 'Broker -> upstream direto via TGW privado.' },
  { value: 'connector', label: 'Connector', desc: 'Túnel WSS reverso (apps em rede privada).' },
]

const IDENTITY_OPTIONS: { value: IdentityMode; label: string; desc: string }[] = [
  { value: 'header', label: 'Header', desc: 'X-Forwarded-User, X-Forwarded-Email.' },
  { value: 'oidc', label: 'OIDC', desc: 'App e OIDC client do broker.' },
  { value: 'mtls', label: 'mTLS', desc: 'Cert client por user via broker CA.' },
]

function parseHost(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase()
  } catch {
    return null
  }
}

export function Step3Routing({ data, publicHostname, dnsLookup, onChange }: Props) {
  // Auto-fill upstream_url com proposed_upstream_url do dns-lookup quando:
  //  - dnsLookup retornou conflict valido (record_type !== 'NONE')
  //  - tem proposed_upstream_url
  //  - upstream_url do user esta vazio (nao sobrescreve edicao manual)
  // User pode editar livre depois (sem re-auto-fill).
  useEffect(() => {
    if (data.upstream_url) return
    const proposed = dnsLookup?.proposed_upstream_url
    if (!proposed) return
    if (dnsLookup?.record_type === 'NONE') return
    onChange({ ...data, upstream_url: proposed })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dnsLookup?.record_type, dnsLookup?.proposed_upstream_url])

  // upstream_url.host == public_hostname → loop infinito apos migracao DNS Lote 3.
  // Backend Pydantic valida tambem (defense in depth), mas mostrar inline aqui
  // evita ida-volta no submit. Recomendamos ALB legacy direto via DNS publico
  // AWS (bypassa Route53 do tenant) ou IP interno via connector.
  const upstreamHost = parseHost(data.upstream_url)
  const upstreamCircular =
    upstreamHost != null
    && publicHostname != null
    && upstreamHost === publicHostname.toLowerCase()
  const upstreamError = upstreamCircular
    ? `upstream aponta pro próprio hostname público (${publicHostname}) — vai criar loop após migração DNS. Use o backend real (ex: alb-XXX.us-east-1.elb.amazonaws.com) ou IP interno via connector.`
    : null

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <div>
          <Title order={4}>Como o broker se conecta ao upstream</Title>
        </div>

        <Radio.Group
          label="Protocolo"
          required
          value={data.protocol}
          onChange={(v) => onChange({ ...data, protocol: v as Protocol })}
        >
          <Stack gap={6} mt={6}>
            {PROTOCOL_OPTIONS.map((opt) => (
              <Radio
                key={opt.value}
                value={opt.value}
                label={
                  <span>
                    <strong>{opt.label}</strong>{' '}
                    <Text component="span" size="xs" c="dimmed">{opt.desc}</Text>
                  </span>
                }
              />
            ))}
          </Stack>
        </Radio.Group>

        <Radio.Group
          label="Routing mode"
          required
          value={data.routing_mode}
          onChange={(v) => onChange({ ...data, routing_mode: v as RoutingMode })}
        >
          <Stack gap={6} mt={6}>
            {ROUTING_OPTIONS.map((opt) => (
              <Radio
                key={opt.value}
                value={opt.value}
                label={
                  <span>
                    <strong>{opt.label}</strong>{' '}
                    <Text component="span" size="xs" c="dimmed">{opt.desc}</Text>
                  </span>
                }
              />
            ))}
          </Stack>
        </Radio.Group>

        <Radio.Group
          label="Identity injection"
          required
          value={data.identity_mode}
          onChange={(v) => onChange({ ...data, identity_mode: v as IdentityMode })}
        >
          <Stack gap={6} mt={6}>
            {IDENTITY_OPTIONS.map((opt) => (
              <Radio
                key={opt.value}
                value={opt.value}
                label={
                  <span>
                    <strong>{opt.label}</strong>{' '}
                    <Text component="span" size="xs" c="dimmed">{opt.desc}</Text>
                  </span>
                }
              />
            ))}
          </Stack>
        </Radio.Group>

        {data.identity_mode === 'oidc' ? (
          <TagsInput
            label="Redirect URIs (OIDC)"
            placeholder="https://portal.example.com/oauth/callback"
            value={data.oidc_redirect_uris}
            onChange={(value) => onChange({ ...data, oidc_redirect_uris: value })}
            description="URIs aceitas pelo broker como callback OIDC. Press Enter para adicionar."
          />
        ) : null}

        {dnsLookup
          && dnsLookup.record_type !== 'NONE'
          && dnsLookup.proposed_upstream_url ? (
          <Alert
            color="teal"
            variant="light"
            icon={<IconInfoCircle size={16} />}
            title="Upstream detectado automaticamente"
          >
            <Stack gap={6}>
              <Text size="sm">
                Encontramos um registro DNS legacy{' '}
                <code>{dnsLookup.record_type}</code> apontando pra{' '}
                <code>{dnsLookup.target}</code> ({dnsLookup.target_kind}).
              </Text>
              {data.upstream_url !== dnsLookup.proposed_upstream_url ? (
                <Group gap={4}>
                  <Text size="sm">Sugestão:</Text>
                  <code>{dnsLookup.proposed_upstream_url}</code>
                  <Anchor
                    size="sm"
                    component="button"
                    type="button"
                    onClick={() =>
                      onChange({
                        ...data,
                        upstream_url: dnsLookup.proposed_upstream_url!,
                      })
                    }
                  >
                    Usar essa sugestão
                  </Anchor>
                </Group>
              ) : (
                <Text size="sm" c="dimmed">
                  Campo Upstream URL já esta com o valor detectado.
                </Text>
              )}
            </Stack>
          </Alert>
        ) : null}

        <TextInput
          label="Upstream URL"
          placeholder="https://alb-XXX.us-east-1.elb.amazonaws.com/ ou https://10.0.0.5:8080/"
          required
          value={data.upstream_url}
          onChange={(e) => onChange({ ...data, upstream_url: e.currentTarget.value })}
          error={upstreamError}
          ff="JetBrains Mono, monospace"
          description="Endpoint backend real do app. NÃO use o public_hostname (vira loop após migração DNS) — use o ALB legacy direto ou IP interno."
        />

        <Switch
          label="Visível a todos da organização"
          description="Quando ativo, todos os membros do tenant dono veem esta app sem necessidade de permissão explícita. Desative para exigir grant individual app:<slug>:access."
          checked={data.org_default_visible}
          onChange={(e) => onChange({ ...data, org_default_visible: e.currentTarget.checked })}
        />

        <Switch
          label="Privado (restrito à organização do app)"
          description="Quando ativado, só funcionários vinculados à organização dona do app acessam. Desativado (padrão) = público: qualquer funcionário acessa."
          checked={data.is_private}
          onChange={(e) => onChange({ ...data, is_private: e.currentTarget.checked })}
        />

        {(!data.org_default_visible || data.required_permissions.length > 0) ? (
          <Alert
            color="yellow"
            variant="light"
            icon={<IconAlertTriangle size={16} />}
          >
            Apenas usuários com a permissão correspondente verão esta app.
          </Alert>
        ) : null}

        <TagsInput
          label="Permissões necessárias (opcional)"
          placeholder="ex: financeiro:read"
          value={data.required_permissions}
          onChange={(value) => onChange({ ...data, required_permissions: value })}
          description="Gate adicional: users do tenant precisam ter ao menos uma dessas permissions. Deixe vazio para sem restrição extra."
        />

        <TimeWindowFieldset
          value={data.allowed_hours}
          bypassRoles={data.bypass_time_window_roles}
          onChange={(allowed_hours, bypass_time_window_roles) =>
            onChange({ ...data, allowed_hours, bypass_time_window_roles })
          }
        />
      </Stack>
    </Card>
  )
}

const DEFAULT_TIME_WINDOW: TimeWindow = {
  start: '08:00',
  end: '18:00',
  tz: 'America/Sao_Paulo',
  weekdays_only: false,
  tz_source: 'app',
}

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'gestor', label: 'Gestor' },
  { value: 'owner', label: 'Owner' },
  { value: 'devops', label: 'DevOps' },
]

interface TimeWindowFieldsetProps {
  value: TimeWindow | null
  bypassRoles: string[]
  onChange: (value: TimeWindow | null, bypassRoles: string[]) => void
}

export function TimeWindowFieldset({ value, bypassRoles, onChange }: TimeWindowFieldsetProps) {
  const enabled = value !== null
  return (
    <Stack gap="xs">
      <Switch
        label={
          <Group gap={6}>
            <IconClockHour4 size={14} />
            <span>Restringir horário de acesso</span>
          </Group>
        }
        description="Quando ativo, usuários só podem acessar dentro da janela. Admin/manager/gestor passam fora do horário por default."
        checked={enabled}
        onChange={(e) =>
          onChange(
            e.currentTarget.checked ? DEFAULT_TIME_WINDOW : null,
            bypassRoles,
          )
        }
      />
      <Collapse in={enabled}>
        {value ? (
          <Stack gap="xs" pl="md">
            <Group grow align="flex-end">
              <TextInput
                label="Início"
                type="time"
                value={value.start}
                onChange={(e) =>
                  onChange({ ...value, start: e.currentTarget.value }, bypassRoles)
                }
              />
              <TextInput
                label="Fim"
                type="time"
                value={value.end}
                onChange={(e) =>
                  onChange({ ...value, end: e.currentTarget.value }, bypassRoles)
                }
              />
            </Group>
            <Stack gap={4}>
              <Text size="sm" fw={500}>Fuso de referência da janela</Text>
              <SegmentedControl
                data={[
                  { value: 'app', label: 'Fuso da app (fixo)' },
                  { value: 'user', label: 'Fuso do usuário' },
                ]}
                value={value.tz_source ?? 'app'}
                onChange={(v) =>
                  onChange(
                    { ...value, tz_source: v as 'app' | 'user' },
                    bypassRoles,
                  )
                }
              />
              <Text size="xs" c="dimmed">
                {(value.tz_source ?? 'app') === 'app'
                  ? 'A janela e avaliada no fuso fixo definido abaixo, igual pra todos os usuários.'
                  : 'A janela e avaliada no fuso de cada usuário (cadastrado em /perfil; fallback: tenant -> Sao_Paulo).'}
              </Text>
            </Stack>
            <TimezoneSelect
              label={
                (value.tz_source ?? 'app') === 'user'
                  ? 'Fuso fallback (quando user não tem)'
                  : 'Fuso da app'
              }
              value={value.tz}
              onChange={(v) =>
                onChange({ ...value, tz: v ?? 'America/Sao_Paulo' }, bypassRoles)
              }
              allowEmpty={false}
            />
            <Switch
              label="Apenas dias uteis (seg-sex)"
              checked={value.weekdays_only}
              onChange={(e) =>
                onChange(
                  { ...value, weekdays_only: e.currentTarget.checked },
                  bypassRoles,
                )
              }
            />
            <MultiSelect
              label="Papéis que ignoram o horário"
              description="Usuários com qualquer um destes papéis acessam fora do horário sem bloqueio."
              data={ROLE_OPTIONS}
              value={bypassRoles}
              onChange={(v) => onChange(value, v)}
              searchable
              clearable
            />
          </Stack>
        ) : null}
      </Collapse>
    </Stack>
  )
}
